import { useEffect, useRef, useState } from 'react'
import { api, ErroreApi } from '../servizi/api.ts'
import { Attesa, Avviso } from '../componenti/Pezzi.tsx'
import { Bottone, Etichetta, Modale, Tabella } from '../componenti/base.tsx'
import { scaricaCsv } from '../servizi/csv.ts'

/**
 * Amministrazione > Utenti > «Importa da foglio» (464, richiesta della Direzione del 17/10/2026:
 * «decine di persone da registrare, per poter iniziare facendo la formazione»).
 *
 * Tre passi: il foglio (CSV o XLSX, con il modello da scaricare), l'anteprima riga per riga con gli
 * errori e la possibilita' di lasciare fuori delle righe, il riepilogo con l'esito dell'email e, per
 * ognuno, il codice da dettare. Collegamento e codice esistono solo in questa finestra: nel database
 * restano le impronte. Per questo il riepilogo si scarica, in due file separati, prima di chiudere.
 *
 * Dall'1/11/2026 (520) l'email del foglio e' l'email personale, e le colonne «crea casella» e «indirizzo
 * CIDIS» danno la casella CIDIS come in «Aggiungi una persona»: l'anteprima dice con quale indirizzo
 * entrera' ciascuno. Dal 2/11/2026 (533) la colonna «è esterno»: con «sì» il collaboratore esterno entra
 * con l'indirizzo @esterni.cidisonlus.org, come con la spunta «È esterno?».
 */

type Riga = {
  numero: number
  valori: {
    nome: string; cognome: string; email: string; esterno: string; crea_casella: string; indirizzo_cidis: string
    ruolo: string; ambito: string; scadenza: string; rapporto: string; sede: string
  }
  errori: string[]
  avvisi: string[]
  lettura: {
    ruolo: { codice: string; nome: string } | null
    ambito: { tipo: string; id: string | null; nome: string } | null
    scadenza: string | null
    rapporto: 'interno' | 'esterno' | null
    rapporto_dal_ruolo: boolean
    sede: { id: string; nome: string } | null
    crea_casella: boolean | null
    crea_casella_predefinita: boolean
    indirizzo_cidis: string | null
    /** «è esterno» (533). */
    esterno: boolean
  }
}
type Anteprima = {
  formato?: 'csv' | 'xlsx'; avvisi?: string[]; colonne_ignorate?: string[]; righe_nel_foglio?: number; righe: Riga[]
}
type Persona = {
  numero: number; id: string; nome: string; cognome: string; email: string; ruolo: string; ambito: string
  /** L'email personale, dove parte l'invito, e la casella CIDIS creata (520). */
  email_personale: string; casella: string | null
  codice_da_consegnare_a_voce: string; collegamento: string; scade_il: string
  email_inviata: boolean; motivo_email: string | null
}
type Ruolo = { codice: string; nome: string; assegnabile: boolean; ambiti_ammessi: string[]; scadenza_max_giorni: number | null }

const MASSIMO_BYTE = 3 * 1024 * 1024
const MASSIMO_RIGHE = 200

const messaggio = (e: unknown) =>
  e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.'
const giorno = (v: string | null) => (v ? v.slice(0, 10).split('-').reverse().join('/') : '')
const quando = (v: string) => new Date(v).toLocaleString('it-IT', {
  weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
})

/** Il file in base64, a pezzi: `btoa` su un file intero supera la pila con i file grandi. */
async function inBase64(file: File): Promise<string> {
  const byte = new Uint8Array(await file.arrayBuffer())
  let testo = ''
  for (let i = 0; i < byte.length; i += 0x8000) testo += String.fromCharCode(...byte.subarray(i, i + 0x8000))
  return btoa(testo)
}


/** Il codice a gruppi di tre: si detta meglio, ed Excel non toglie lo zero iniziale. */
const codiceLeggibile = (c: string) => `${c.slice(0, 3)} ${c.slice(3)}`

export function ImportaUtenti({ chiudi, importati }: {
  chiudi: () => void
  /** Dopo la conferma: l'elenco si ricarica. La finestra resta aperta sul riepilogo. */
  importati: () => void
}) {
  const [fase, setFase] = useState<'foglio' | 'anteprima' | 'riepilogo'>('foglio')
  const [ruoli, setRuoli] = useState<Ruolo[]>([])
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const [nomeFile, setNomeFile] = useState('')
  const [anteprima, setAnteprima] = useState<Anteprima | null>(null)
  const [escluse, setEscluse] = useState<Set<number>>(new Set())
  const [ricontrollo, setRicontrollo] = useState(false)
  const [persone, setPersone] = useState<Persona[]>([])
  const [scaricati, setScaricati] = useState(false)
  const [chiusuraChiesta, setChiusuraChiesta] = useState(false)
  const ingresso = useRef<HTMLInputElement>(null)

  useEffect(() => {
    api.get<{ ruoli: Ruolo[] }>('/amministrazione/ruoli').then((r) => setRuoli(r.ruoli)).catch(() => {})
  }, [])

  async function leggi(file: File) {
    setErrore(null)
    if (file.size > MASSIMO_BYTE) {
      setErrore('Il file è troppo grande: un foglio con qualche centinaio di persone pesa molto meno. '
        + 'Salvalo con le sole colonne del modello.')
      return
    }
    setInCorso(true)
    try {
      const esito = await api.post<Anteprima>('/amministrazione/utenti/importazione/anteprima',
        { nome_file: file.name, contenuto: await inBase64(file) })
      setNomeFile(file.name)
      setAnteprima(esito)
      // Le righe con errori partono escluse: si importa solo cio' che e' pronto.
      setEscluse(new Set(esito.righe.filter((r) => r.errori.length).map((r) => r.numero)))
      setFase('anteprima')
    } catch (e) {
      setErrore(messaggio(e))
    } finally {
      setInCorso(false)
      if (ingresso.current) ingresso.current.value = ''
    }
  }

  /**
   * Includere o escludere una riga cambia l'esito delle altre (due righe con la stessa email): si
   * ricontrollano le righe incluse.
   */
  async function cambiaInclusione(numero: number, includi: boolean) {
    if (!anteprima) return
    const nuove = new Set(escluse)
    if (includi) nuove.delete(numero); else nuove.add(numero)
    setEscluse(nuove)
    const incluse = anteprima.righe.filter((r) => !nuove.has(r.numero))
    if (!incluse.length) return
    setRicontrollo(true); setErrore(null)
    try {
      const esito = await api.post<Anteprima>('/amministrazione/utenti/importazione/anteprima',
        { righe: incluse.map((r) => ({ numero: r.numero, ...r.valori })) })
      const perNumero = new Map(esito.righe.map((r) => [r.numero, r]))
      setAnteprima((a) => a && { ...a, righe: a.righe.map((r) => perNumero.get(r.numero) ?? r) })
    } catch (e) {
      setErrore(messaggio(e))
    } finally {
      setRicontrollo(false)
    }
  }

  async function importa() {
    if (!anteprima) return
    setErrore(null); setInCorso(true)
    const incluse = anteprima.righe.filter((r) => !escluse.has(r.numero))
    try {
      const esito = await api.post<{ persone: Persona[] }>('/amministrazione/utenti/importazione', {
        righe: incluse.map((r) => ({ numero: r.numero, ...r.valori })),
        righe_nel_foglio: anteprima.righe_nel_foglio ?? anteprima.righe.length,
      })
      setPersone(esito.persone)
      setFase('riepilogo')
      importati()
    } catch (e) {
      // Nel frattempo una riga non va piu': torna l'anteprima aggiornata e non si e' creato nessuno.
      if (e instanceof ErroreApi && e.codice === 'RIGHE_DA_CORREGGERE') {
        const perNumero = new Map((e.corpo as { righe: Riga[] }).righe.map((r) => [r.numero, r]))
        setAnteprima((a) => a && { ...a, righe: a.righe.map((r) => perNumero.get(r.numero) ?? r) })
      }
      setErrore(messaggio(e))
    } finally {
      setInCorso(false)
    }
  }

  function scaricaCodici() {
    scaricaCsv(`codici-inviti-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Cognome', 'Nome', 'Email con cui entra', 'Email personale', 'Codice da dettare', 'Invito valido fino al'],
      persone.map((p) => [p.cognome, p.nome, p.email, p.email_personale, codiceLeggibile(p.codice_da_consegnare_a_voce),
        new Date(p.scade_il).toLocaleString('it-IT')]))
    setScaricati(true)
  }

  function scaricaCollegamenti() {
    scaricaCsv(`collegamenti-inviti-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Cognome', 'Nome', 'Email personale', 'Collegamento', 'Invito valido fino al'],
      persone.filter((p) => !p.email_inviata).map((p) => [p.cognome, p.nome, p.email_personale, p.collegamento,
        new Date(p.scade_il).toLocaleString('it-IT')]))
  }

  const righe = anteprima?.righe ?? []
  const incluse = righe.filter((r) => !escluse.has(r.numero))
  const daCorreggere = incluse.filter((r) => r.errori.length)
  const pronte = incluse.length - daCorreggere.length
  const senzaEmail = persone.filter((p) => !p.email_inviata)

  // Il riepilogo non si chiude per sbaglio: i codici non si rivedono piu'.
  const chiudiFinestra = () => {
    if (fase === 'riepilogo' && !scaricati && !chiusuraChiesta) { setChiusuraChiesta(true); return }
    chiudi()
  }

  const titolo = fase === 'foglio' ? 'Importa da foglio · passo 1 di 3'
    : fase === 'anteprima' ? 'Importa da foglio · passo 2 di 3, anteprima' : 'Importa da foglio · passo 3 di 3, consegna'

  return (
    <Modale titolo={titolo} larga chiudi={chiudiFinestra}
            azioni={fase === 'foglio'
              ? <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
              : fase === 'anteprima'
                ? <>
                  <Bottone variante="discreto" onClick={() => { setFase('foglio'); setAnteprima(null); setErrore(null) }}>
                    Carica un altro foglio
                  </Bottone>
                  <Bottone variante="primario" inCorso={inCorso}
                           disabled={ricontrollo || pronte === 0 || daCorreggere.length > 0}
                           onClick={() => void importa()}>
                    {pronte === 1 ? 'Importa 1 persona' : `Importa ${pronte} persone`}
                  </Bottone>
                </>
                : <Bottone variante={chiusuraChiesta ? 'critico' : 'primario'} onClick={chiudiFinestra}>
                  {chiusuraChiesta ? 'Chiudi comunque' : 'Fatto'}
                </Bottone>}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      {fase === 'foglio' && (
        <>
          <p className="nota-modulo">
            Una riga per persona, con le colonne del modello. Il gestionale crea l’utenza, assegna il
            ruolo e prepara l’invito di ciascuno; prima di creare qualcosa ti mostra l’anteprima.
          </p>
          <ul className="operatori-conseguenze">
            <li>
              <strong>nome</strong>, <strong>cognome</strong>, <strong>email personale</strong>: obbligatori. All’email
              personale arrivano l’invito e poi il recupero della password.
            </li>
            <li>
              <strong>è esterno</strong>: «sì» per i collaboratori esterni che non devono avere la casella
              cidisonlus.org. Entrano con l’iniziale del nome e il cognome @esterni.cidisonlus.org, hanno lì la loro
              casella, che scrive e riceve solo dentro CIDIS, e il rapporto esterno; la scadenza è facoltativa e il ruolo
              può essere qualunque. Vuota vale «no».
            </li>
            <li>
              <strong>crea casella</strong>: «sì» o «no». Se è vuota, sì per i rapporti interni e no per gli esterni.
              Con la casella la persona entra con l’indirizzo CIDIS. Per i collaboratori esterni resta vuota.
            </li>
            <li>
              <strong>indirizzo CIDIS</strong>: facoltativo. Se è vuoto si propone l’iniziale del nome e il cognome, per
              esempio dramondetti@cidisonlus.org (per un collaboratore esterno mrossi@esterni.cidisonlus.org); se è già
              preso, la riga lo dice e propone alternative.
            </li>
            <li><strong>ruolo</strong>: il nome o la sigla, fra quelli qui sotto.</li>
            <li>
              <strong>ambito</strong>, dove vale il ruolo: «tutta CIDIS», oppure il nome di una sede, di
              un’area o di un progetto come compare nel gestionale. Se due hanno lo stesso nome, scrivi
              per esempio «sede: Perugia» o «progetto: Perugia».
            </li>
            <li>
              <strong>scadenza</strong>: per gli esterni e per i ruoli a tempo è obbligatoria (entro
              dodici mesi), per i collaboratori esterni facoltativa; negli altri casi si può lasciare vuota. Per
              esempio 31/12/2026.
            </li>
            <li>
              <strong>rapporto</strong>: «interno» o «esterno». Se è vuoto si ricava dal ruolo.
            </li>
            <li>
              <strong>sede</strong>: la sede CIDIS in cui lavora. Se l’ambito è una sede, si può lasciare vuota.
            </li>
          </ul>
          {ruoli.length > 0 && (
            <p className="nota-modulo">
              Ruoli che si assegnano: {ruoli.filter((r) => r.assegnabile && r.codice !== 'HM').map((r) => `${r.nome} (${r.codice})`).join(', ')}.
            </p>
          )}
          <p className="nota-modulo">
            Al massimo {MASSIMO_RIGHE} persone alla volta. Formati: Excel (.xlsx) oppure CSV, con il punto
            e virgola o la virgola.
          </p>
          <div className="riga operatori-sezione">
            <a className="bottone bottone-secondario" href="/api/amministrazione/utenti/importazione/modello" download>
              Scarica il modello
            </a>
            <Bottone variante="primario" inCorso={inCorso} onClick={() => ingresso.current?.click()}>
              Scegli il foglio
            </Bottone>
            <input ref={ingresso} type="file" hidden
                   accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                   onChange={(e) => { const f = e.target.files?.[0]; if (f) void leggi(f) }} />
          </div>
          {inCorso && <Attesa testo="Leggo il foglio e controllo le righe…" />}
        </>
      )}

      {fase === 'anteprima' && anteprima && (
        <>
          <p className="nota-modulo">
            {nomeFile}: {righe.length === 1 ? '1 persona' : `${righe.length} persone`}.{' '}
            Pronte da importare: {pronte}. {daCorreggere.length > 0 && `Da correggere: ${daCorreggere.length}. `}
            {escluse.size > 0 && `Lasciate fuori: ${escluse.size}.`}
          </p>
          {anteprima.avvisi?.map((a) => <Avviso key={a} tipo="attenzione">{a}</Avviso>)}
          {!!anteprima.colonne_ignorate?.length && (
            <Avviso tipo="informazione">
              Colonne che non uso: {anteprima.colonne_ignorate.map((c) => `«${c}»`).join(', ')}.
            </Avviso>
          )}
          {daCorreggere.length > 0 && (
            <Avviso tipo="attenzione">
              {daCorreggere.length === 1 ? 'Una riga inclusa ha' : `${daCorreggere.length} righe incluse hanno`} ancora
              qualcosa da correggere: lasciale fuori, oppure correggi il foglio e caricalo di nuovo.
            </Avviso>
          )}
          {ricontrollo && <Attesa testo="Ricontrollo le righe…" />}
          <Tabella intestazioni={['Importa', 'Riga', 'Persona', 'Che cosa potrà fare', 'Esito']}>
            {righe.map((r) => {
              const fuori = escluse.has(r.numero)
              const l = r.lettura
              return (
                <tr key={r.numero} className={fuori ? 'operatori-riga-esclusa' : undefined}>
                  <td data-etichetta="Importa">
                    <input type="checkbox" checked={!fuori} disabled={ricontrollo || inCorso}
                           aria-label={`Importa la riga ${r.numero}`}
                           onChange={(e) => void cambiaInclusione(r.numero, e.target.checked)} />
                  </td>
                  <td data-etichetta="Riga" className="mono">{r.numero}</td>
                  {/* Un solo figlio per cella: sotto gli 860 px la cella e' una riga flessibile, e
                      piu' figli finirebbero affiancati invece che uno sotto l'altro. */}
                  <td data-etichetta="Persona">
                    <div>
                      <div className="voce-principale">{r.valori.cognome} {r.valori.nome}</div>
                      <div className="piccolo tenue">{r.valori.email}</div>
                      {/* Con quale indirizzo entrera' (520). */}
                      {l.esterno && (
                        <div className="piccolo">
                          Collaboratore esterno{l.indirizzo_cidis ? `: entra con ${l.indirizzo_cidis}` : ''}
                        </div>
                      )}
                      {l.crea_casella && !l.esterno && (
                        <div className="piccolo">
                          Casella {l.indirizzo_cidis ?? r.valori.indirizzo_cidis ?? ''}
                          {l.crea_casella_predefinita ? ' (rapporto interno)' : ''}
                        </div>
                      )}
                      {l.crea_casella === false && !l.esterno && <div className="piccolo tenue">Senza casella CIDIS</div>}
                    </div>
                  </td>
                  <td data-etichetta="Che cosa potrà fare"><div>
                    <div>{l.ruolo?.nome ?? r.valori.ruolo}{l.ambito ? ` · ${l.ambito.nome}` : r.valori.ambito ? ` · ${r.valori.ambito}` : ''}</div>
                    <div className="piccolo tenue">
                      {[
                        l.rapporto && `${l.rapporto === 'interno' ? 'Interno' : 'Esterno'}${l.rapporto_dal_ruolo ? ' (dal ruolo)' : ''}`,
                        l.sede && `lavora a ${l.sede.nome}`,
                        l.scadenza ? `fino al ${giorno(l.scadenza)}` : r.valori.scadenza && `scadenza: ${r.valori.scadenza}`,
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </div></td>
                  <td data-etichetta="Esito"><div>
                    {fuori && <div><Etichetta>Lasciata fuori</Etichetta></div>}
                    {r.errori.length > 0
                      ? <ul className="operatori-errori-riga">{r.errori.map((x) => <li key={x}>{x}</li>)}</ul>
                      : !fuori && <Etichetta tono="positivo">Pronta</Etichetta>}
                    {r.avvisi.map((x) => <p key={x} className="piccolo tenue">{x}</p>)}
                  </div></td>
                </tr>
              )
            })}
          </Tabella>
          {pronte > 0 && daCorreggere.length === 0 && (
            <p className="nota-modulo">
              Importando si creano {pronte === 1 ? 'un’utenza' : `${pronte} utenze`} con il loro incarico e si
              preparano gli inviti; le email partono subito. Nel passo dopo trovi i codici da dettare.
            </p>
          )}
        </>
      )}

      {fase === 'riepilogo' && (
        <>
          <Avviso tipo="successo">
            {persone.length === 1 ? 'Ho creato 1 utenza' : `Ho creato ${persone.length} utenze`} con il loro incarico.
            Email d’invito partite: {persone.length - senzaEmail.length}
            {senzaEmail.length > 0 && `; da mandare tu: ${senzaEmail.length}`}.
          </Avviso>
          <Avviso tipo="attenzione">
            <strong>Scarica i codici prima di chiudere:</strong> dopo non si possono più rivedere (nel
            gestionale restano solo in forma non leggibile). Per chi lo perde, dalla sua riga
            nell’elenco si manda un nuovo invito. I file contengono dati d’accesso: conservali con cura,
            non inoltrarli e cancellali quando hai consegnato tutto.
          </Avviso>
          <p className="nota-modulo">
            Il codice si consegna <strong>a voce</strong>, di persona o per telefono, oppure comunque
            separato dal collegamento: mai nello stesso messaggio. Chi trovasse solo uno dei due non può
            entrare al posto della persona. Per questo i codici e i collegamenti si scaricano in due file
            diversi.
          </p>
          {persone[0] && (
            <p className="nota-modulo">
              Gli inviti valgono fino a {quando(persone[0].scade_il)}: chi non entra in tempo riceve un nuovo
              invito dalla sua riga nell’elenco.
            </p>
          )}
          <div className="riga operatori-sezione">
            <Bottone variante="primario" onClick={scaricaCodici}>Scarica i codici da dettare</Bottone>
            {senzaEmail.length > 0 && (
              <Bottone onClick={scaricaCollegamenti}>Scarica i collegamenti da mandare</Bottone>
            )}
          </div>
          {chiusuraChiesta && (
            <Avviso tipo="attenzione">
              Non hai scaricato i codici: chiudendo non li rivedrai più. Scaricali, oppure chiudi comunque
              e manda poi un nuovo invito a chi ne ha bisogno.
            </Avviso>
          )}
          <Tabella intestazioni={['Persona', 'Email d’invito', 'Codice da dettare', 'Collegamento']}>
            {persone.map((p) => (
              <tr key={p.id}>
                <td data-etichetta="Persona"><div>
                  <div className="voce-principale">{p.cognome} {p.nome}</div>
                  <div className="piccolo tenue">{p.email}</div>
                  {p.casella && <div className="piccolo tenue">invito a {p.email_personale}</div>}
                  <div className="piccolo tenue">{p.ruolo} · {p.ambito}</div>
                </div></td>
                <td data-etichetta="Email d’invito"><div>
                  {p.email_inviata
                    ? <Etichetta tono="positivo">Partita</Etichetta>
                    : <>
                      <Etichetta tono="attenzione">Non partita</Etichetta>
                      {p.motivo_email && <div className="piccolo tenue">{p.motivo_email}</div>}
                    </>}
                </div></td>
                <td data-etichetta="Codice da dettare">
                  <span className="operatori-codice-riga" aria-label={`Codice di ${p.nome}`}>
                    {codiceLeggibile(p.codice_da_consegnare_a_voce)}
                  </span>
                </td>
                <td data-etichetta="Collegamento">
                  {p.email_inviata
                    ? <span className="piccolo tenue">Nell’email</span>
                    : <input className="operatori-collegamento" readOnly value={p.collegamento}
                             aria-label={`Collegamento dell’invito di ${p.nome} ${p.cognome}`}
                             onFocus={(e) => e.currentTarget.select()} />}
                </td>
              </tr>
            ))}
          </Tabella>
        </>
      )}
    </Modale>
  )
}
